import { IconButton, IconFont } from '@/components';
import { createTaggingTask } from '@/services/custom/extractConfig';
import { create, useModal } from '@ebay/nice-modal-react';
import { useMutation } from '@umijs/max';
import { Drawer, message, Progress, Upload } from 'antd';
import { useState } from 'react';
import { DrawerHeader } from '../../../components';
import { useAnnotatedTaskId } from '../../stores';
import {
  APPLICATION_REG,
  DEFAULT_PROPS,
  DOC_REG,
  IMAGE_REG,
  SIZE_100MB,
  SIZE_20MB,
  SIZE_2MB,
  TXT_REG,
  ZIP_REG,
} from './const';
import type {
  RcFileWithResponse,
  Response,
  UploadFileWithDeprecated,
} from './type';

const { Dragger } = Upload;

export const NoteCreator = create<{ refresh: () => void }>((props) => {
  // props
  const { refresh } = props;
  // state
  const [fileList, setFileList] = useState<
    UploadFileWithDeprecated<Response>[]
  >([]);
  // modal
  const { visible, remove } = useModal();
  // shared state
  const annotatedTaskId = useAnnotatedTaskId()[0];
  // query
  const { mutate: create, isLoading } = useMutation({
    mutationFn: createTaggingTask,
    onSuccess(res) {
      if (res.code === 0) {
        for (const file of fileList) {
          file._deprecated = true;
        }

        refresh();
      }
    },
  });
  // methods
  const createIcon = (type: string) => (
    <IconFont className="text-2xl" type={type} />
  );
  const createResult = (file: UploadFileWithDeprecated) => {
    const { percent, response } = file;

    if (response) {
      const { code } = response;

      if (code === 0) {
        return createIcon('icon-liebiaochenggong');
      }
      return createIcon('icon-liebiaoshibai');
    }

    return (
      <Progress type="circle" percent={percent} size={24} showInfo={false} />
    );
  };
  const createErrorResponse = (file: RcFileWithResponse) =>
    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    (file.response = {
      code: -1,
      data: null,
      msg: 'size limited',
    });

  return (
    <Drawer
      title={<DrawerHeader title="上传文件" onClose={remove} />}
      open={visible}
      maskClosable={false}
      width={480}
      closeIcon={null}
      onClose={remove}
    >
      <section className="flex flex-col gap-3 h-full overflow-auto">
        <Dragger
          {...DEFAULT_PROPS}
          className="sticky top-0 z-10 bg-white"
          disabled={isLoading}
          fileList={fileList}
          beforeUpload={(file) =>
            new Promise((resolve, reject) => {
              const { type, size } = file;

              if (type.match(ZIP_REG) && size > SIZE_100MB) {
                createErrorResponse(file);
                message.warning('压缩文件大小不超过100MB');
                reject('压缩文件大小不超过100MB');
                return;
              }

              if (type.match(TXT_REG) && size > SIZE_2MB) {
                createErrorResponse(file);
                message.warning('文本文件大小不超过2MB');
                reject('文本文件大小不超过2MB');
                return;
              }

              if (type.match(IMAGE_REG) && size > SIZE_20MB) {
                createErrorResponse(file);
                message.warning('图片文件大小不超过20MB');
                reject('图片文件大小不超过20MB');
                return;
              }

              if (type.match(DOC_REG) && size > SIZE_100MB) {
                createErrorResponse(file);
                message.warning('文档文件大小不超过100MB');
                reject('文档文件大小不超过100MB');
                return;
              }

              resolve();
              // ! 不做批量校验了 状态管理太麻烦了
              // ! 如果遇到大文件再考虑file.stream
              // file
              //   .arrayBuffer()
              //   .then((buffer) => md5(new Uint8Array(buffer)))
              //   .then((hash) =>
              //     verifyFileExist([{ file_hash: hash, filename: name }]),
              //   )
              //   .then((res) => {
              //     const { code, data } = res;
              //     if (code === 0) {
              //       const { file_hash } = data;

              //       if (file_hash.length > 0) {
              //         resolve();
              //       } else {
              //         (file as RcFileWithResponse).response = {
              //           code: 0,
              //           data: {
              //             file_hash: file_hash[0],
              //             filename: name,
              //             template_id_suggested: '',
              //           },
              //           msg: 'ok',
              //         };
              //         reject();
              //       }
              //     } else {
              //       reject();
              //     }
              //   });
            })
          }
          onChange={(info) => {
            let resolved = true;
            const fileList = info.fileList.map<
              UploadFileWithDeprecated<Response>
            >((file) => {
              const { originFileObj } = file;

              if (
                !file.response &&
                originFileObj &&
                'response' in originFileObj
              ) {
                file.response = originFileObj.response;
                file.status = 'done';
              }

              if (!file.response) {
                resolved = false;
              }

              return file;
            });

            if (resolved) {
              create({
                parent_id: annotatedTaskId,
                module: 'TAGGING',
                file_hash_list: fileList
                  .filter(
                    (file) => !file._deprecated || file.response?.code !== 0,
                  )
                  .map((file) => {
                    const { data } = file.response!;

                    return {
                      file_hash_id: data.file_hash,
                      task_name: data.filename,
                    };
                  }),
              });
            }

            setFileList(fileList);
          }}
        >
          <section className="flex flex-col gap-2 justify-center items-center h-44 group">
            <IconFont
              className="mb-1 text-4xl group-hover:text-primary"
              type="icon-shangchuan"
            />
            <p className="text-sm text-secondary-1">点击/拖拽上传一份文件</p>
            <p className="flex flex-col items-center text-xs text-secondary-3">
              <span>支持zip，文件大小不超过100MB；</span>
              <span>支持txt，文件大小不超过2MB；</span>
              <span>支持doc、docx、pdf，文件大小不超过100MB；</span>
              <span>支持png、jpg、jpeg、bmp，文件大小不超过20MB</span>
            </p>
          </section>
        </Dragger>
        {fileList.length > 0 && (
          <ul className="flex flex-col gap-2">
            {fileList.map((file, idx) => {
              const { name } = file;

              return (
                <li
                  className="flex justify-between items-center h-14 px-3 bg-main-bg rounded transition group hover:shadow"
                  key={file.uid}
                >
                  <div className="flex items-center gap-2">
                    <p className="max-w-80 truncate text-sm font-medium text-main">
                      {name}
                    </p>
                    {createResult(file)}
                  </div>
                  <IconButton
                    circle
                    title="删除"
                    className="hidden hover:text-error group-hover:flex"
                    iconType="icon-shanchu1"
                    onClick={() =>
                      setFileList((fileList) => [
                        ...fileList.slice(0, idx),
                        ...fileList.slice(idx + 1),
                      ])
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </Drawer>
  );
});
